/**
 * Lead 追踪工具
 * 用于管理 lead_id 和提交类事件的通用参数
 *
 * 核心概念：
 * - lead_id: 线索级唯一标识，仅在 form_submit_success / chat_lead_success 时生成
 * - 双归因机制: first_intent_id/first_cta_location（首次触点）+ last_intent_id/last_cta_location（最后触点）
 *
 * 规则：
 * - lead_id 在每次成功提交时新生成
 * - first/last intent_id 和 cta_location 从 sessionStorage 读取
 * - UTM 参数从 sessionStorage 读取，不生成
 */

import {
  getFirstIntentId,
  getFirstCtaLocation,
  getLastIntentId,
  getLastCtaLocation
} from './ctaTracking';
import { getUtmParams, getFirstPageUrl, getLandingPage } from './utmParams';
import type { UtmParams } from './utmParams';

/**
 * 生成 lead_id（UUID）
 * 仅在 form_submit_success / chat_lead_success 时调用
 */
export function generateLeadId(): string {
  const leadId = crypto.randomUUID();
  console.log('[Lead Tracking] Generated lead_id:', leadId);
  return leadId;
}

/**
 * 提交类事件的通用参数接口（包含双归因）
 */
export interface SubmitEventParams extends UtmParams {
  lead_id: string;
  // 双归因参数
  first_intent_id: string | null;
  first_cta_location: string | null;
  last_intent_id: string | null;
  last_cta_location: string | null;
  // 渠道
  lead_channel: 'form' | 'chat';
  // 页面上下文
  user_agent: string;
  first_page_url: string;
  landing_page: string;
}

/**
 * 获取提交类事件的通用参数（包含双归因）
 * 用于 form_submit_success 和 chat_lead_success 事件
 *
 * @param leadChannel - 线索来源渠道 ('form' | 'chat')
 * @returns 包含 lead_id, 双归因参数, UTM 等参数的对象
 */
export function getSubmitEventParams(leadChannel: 'form' | 'chat'): SubmitEventParams {
  const utmParams = getUtmParams();

  return {
    // Lead 标识
    lead_id: generateLeadId(),

    // 双归因参数（从 sessionStorage 读取）
    first_intent_id: getFirstIntentId(),
    first_cta_location: getFirstCtaLocation(),
    last_intent_id: getLastIntentId(),
    last_cta_location: getLastCtaLocation(),

    // 渠道
    lead_channel: leadChannel,

    // 页面上下文
    user_agent: navigator.userAgent,
    first_page_url: getFirstPageUrl(),
    landing_page: getLandingPage(),

    // UTM 参数（从 storage 读取）
    ...utmParams,
  };
}

/**
 * 获取表单提交成功事件的参数
 */
export function getFormSubmitSuccessParams(formId: string, formName: string) {
  const baseParams = getSubmitEventParams('form');
  
  return {
    ...baseParams,
    form_id: formId,
    form_name: formName,
    form_submit_time: new Date().toISOString(),
  };
}

/**
 * 获取聊天线索事件的参数（用于 chat_lead 事件）
 */
export function getChatLeadParams(
  chatProvider: string,
  serviceType: string,
  contactMethod: string
) {
  const baseParams = getSubmitEventParams('chat');

  return {
    ...baseParams,
    chat_provider: chatProvider,
    service_type: serviceType,
    contact_method: contactMethod,
    chat_submit_time: new Date().toISOString(),
  };
}

/**
 * 获取聊天线索成功事件的参数（用于 chat_lead_success 事件）
 *
 * 注意：此函数应在 API 成功后调用，使用与 chat_lead 相同的 lead_id
 *
 * @param provider - 聊天提供商名称（如 'creston_chat'）
 * @returns 包含 lead_id, chat_provider, 双归因参数, UTM 等参数的对象
 */
export function getChatLeadSuccessParams(provider: string) {
  const baseParams = getSubmitEventParams('chat');

  return {
    ...baseParams,
    chat_provider: provider,  // <--- 动态传入提供商名称
    event_time: new Date().toISOString(),
  };
}

/**
 * 诊断事件参数接口
 */
export interface DiagnosticEventParams extends UtmParams {
  // 双归因参数（全局变量，从 sessionStorage 读取）
  first_intent_id: string | null;
  first_cta_location: string | null;
  last_intent_id: string | null;
  last_cta_location: string | null;
  // 页面上下文
  landing_page: string;
}

/**
 * 获取诊断事件的通用参数（不包含 lead_id）
 * 用于 form_view, form_start, form_error, chat_engaged 等事件
 *
 * 包含双归因参数（从 sessionStorage 读取）
 */
export function getDiagnosticEventParams(): DiagnosticEventParams {
  const utmParams = getUtmParams();

  return {
    // 双归因参数（全局变量，从 sessionStorage 读取）
    first_intent_id: getFirstIntentId(),
    first_cta_location: getFirstCtaLocation(),
    last_intent_id: getLastIntentId(),
    last_cta_location: getLastCtaLocation(),
    // 页面上下文
    landing_page: getLandingPage(),
    // UTM 参数（从 storage 读取）
    ...utmParams,
  };
}

/**
 * 获取 chat_open 事件的参数（不包含双归因）
 * chat_open 是意图弱信号，只需要基本参数
 */
export function getChatOpenParams(chatProvider: string) {
  const utmParams = getUtmParams();

  return {
    chat_provider: chatProvider,
    chat_open_time: new Date().toISOString(),
    ...utmParams,
  };
}

/**
 * 获取 chat_engaged 事件的参数（包含双归因）
 */
export function getChatEngagedParams(chatProvider: string) {
  const diagnosticParams = getDiagnosticEventParams();

  return {
    ...diagnosticParams,
    chat_provider: chatProvider,
    chat_engaged_time: new Date().toISOString(),
  };
}

